import { type Account, accountStore, clearAccount, getAccount, setAccount } from '@/lib/account';
import { StatusCode } from '@/lib/models';
import { createStore } from '@/lib/store';

/**
 * The one way the portal talks to the server.
 *
 * Four behaviours here are not conveniences, they are the contract the server's
 * envelope was designed around, and each one fails silently if it is dropped:
 *
 *  - **401 means refresh, once.** The server answers an expired token with 401 and
 *    nothing else, so this is the only signal. Concurrent 401s share one refresh —
 *    without that, a page that fires six queries at once burns six refresh tokens.
 *  - **A refresh that lands in a replaced session is discarded.** Between capturing
 *    the token and the response, the user may have signed out or signed in as someone
 *    else. Writing the new access token back then would resurrect a dead session.
 *  - **EULA and ban are 403, not 200.** They redirect rather than render, which is why
 *    the server must not wrap them in a 2xx.
 *  - **Loading is ref-counted.** The progress bar goes away when the *last* request
 *    finishes, not the first.
 */

/**
 * Where the API lives.
 *
 * The portal and the server are separate deployments — the portal is static files on a
 * CDN, the server is a box on a LAN that cabinets can also reach — so this is baked in
 * at build time by `VITE_API_BASE`. It must end in a slash; every path below is
 * appended to it.
 *
 * Defaults to same-origin, which keeps `pnpm dev` working through Vite's proxy and
 * keeps a single-origin deployment behind one reverse proxy working with no config.
 */
const API_SERVER: string = (() => {
  const base = import.meta.env.VITE_API_BASE ?? '/';
  return base.endsWith('/') ? base : `${base}/`;
})();

/** Refresh this long before `exp`, so a request never races its own token expiring. */
const REFRESH_LEAD_TIME_MS = 30_000;

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export const loadingStore = createStore(false);
let loadingCount = 0;

function beginLoading(): void {
  loadingCount += 1;
  loadingStore.set(true);
}

function endLoading(): void {
  loadingCount = Math.max(0, loadingCount - 1);
  if (loadingCount === 0) loadingStore.set(false);
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = API_SERVER + path.replace(/^\//, '');
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function authHeaders(): Record<string, string> {
  const account = getAccount();
  if (!account?.accessToken) return {};
  return { Authorization: `${account.tokenType || 'Bearer'} ${account.accessToken}` };
}

async function parseBody(resp: Response): Promise<unknown> {
  const text = await resp.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Not every endpoint is JSON — the catalogs are, but an error page from a proxy in
    // front of us is not, and the message is more useful than a parse failure.
    return text;
  }
}

interface Envelope {
  data?: unknown;
  status?: { code?: number; message?: string };
}

function asEnvelope(body: unknown): Envelope | null {
  return body && typeof body === 'object' ? (body as Envelope) : null;
}

export async function rawFetch(url: string, init: RequestInit = {}): Promise<Response> {
  return fetch(url, { ...init, headers: { ...authHeaders(), ...(init.headers ?? {}) } });
}

// ---- single-flight refresh ----

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(captured: Account): Promise<string> {
  refreshPromise ??= (async () => {
    const resp = await fetch(`${API_SERVER}api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: captured.refreshToken }),
    });
    const envelope = asEnvelope(await parseBody(resp));
    const current = getAccount();
    // The session that asked for this refresh is gone; whatever came back belongs to
    // nobody. Writing it would sign a signed-out user back in.
    if (!current || current.refreshToken !== captured.refreshToken) {
      throw new Error('session replaced');
    }
    const token = (envelope?.data as { accessToken?: string } | undefined)?.accessToken;
    if (resp.ok && envelope?.status?.code === StatusCode.OK && token) {
      setAccount({ ...current, accessToken: token });
      return token;
    }
    throw new Error('refresh failed');
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// ---- error mapping ----

/** What the shell listens for to send the user to `/eula` or `/banned`. */
export const ACCESS_ERROR_EVENT = 'artemis-account-access-error';

export class ApiError extends Error {
  readonly httpStatus: number;
  readonly code: number | undefined;

  constructor(message: string, httpStatus: number, code: number | undefined) {
    super(message);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
    this.code = code;
  }
}

function fail(resp: Response, body: unknown): never {
  const envelope = asEnvelope(body);
  const code = envelope?.status?.code;

  if (
    code === StatusCode.EULA_REQUIRED ||
    code === StatusCode.ACCOUNT_BANNED ||
    code === StatusCode.PASSWORD_CHANGE_REQUIRED
  ) {
    // An event rather than a navigation: this module has no router, and the shell is
    // the only place that knows whether the user is already on the page in question.
    window.dispatchEvent(
      new CustomEvent(ACCESS_ERROR_EVENT, {
        detail:
          code === StatusCode.EULA_REQUIRED
            ? 'EULA_REQUIRED'
            : code === StatusCode.ACCOUNT_BANNED
              ? 'ACCOUNT_BANNED'
              : 'PASSWORD_CHANGE_REQUIRED',
      }),
    );
  }

  const message =
    envelope?.status?.message ??
    (typeof body === 'string' && body ? body : `${resp.status} ${resp.statusText}`);

  // A 401 that survived the refresh above means the refresh token is dead too. Drop
  // the session so the shell renders the signed-out view instead of looping.
  if (resp.status === 401 && getAccount()) clearAccount();

  throw new ApiError(message, resp.status, code);
}

// ---- the request itself ----

async function perform(
  method: string,
  path: string,
  params?: QueryParams,
  body?: unknown,
): Promise<unknown> {
  const url = buildUrl(path, params);
  const isRefreshCall = path.includes('api/auth/refresh');
  const send = () =>
    fetch(url, {
      method,
      headers: {
        ...authHeaders(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  beginLoading();
  try {
    let resp = await send();

    if (resp.status === 401 && !isRefreshCall) {
      const account = getAccount();
      if (account?.refreshToken) {
        try {
          await refreshAccessToken(account);
          resp = await send();
        } catch {
          // Refreshing failed. Fall through with the original 401 so the user gets the
          // real reason rather than "session replaced".
        }
      }
    }

    const parsed = await parseBody(resp);
    if (!resp.ok) fail(resp, parsed);
    return parsed;
  } finally {
    endLoading();
  }
}

export const api = {
  get: <T = unknown>(path: string, params?: QueryParams) =>
    perform('GET', path, params) as Promise<T>,
  post: <T = unknown>(path: string, data?: unknown, params?: QueryParams) =>
    perform('POST', path, params, data) as Promise<T>,
  put: <T = unknown>(path: string, data?: unknown, params?: QueryParams) =>
    perform('PUT', path, params, data) as Promise<T>,
  delete: <T = unknown>(path: string, params?: QueryParams, data?: unknown) =>
    perform('DELETE', path, params, data) as Promise<T>,
  /** Exports download as a file; they are not JSON and must not be parsed as it. */
  blob: async (path: string, params?: QueryParams): Promise<Blob> => {
    const resp = await rawFetch(buildUrl(path, params));
    if (!resp.ok) throw new ApiError(`${resp.status} ${resp.statusText}`, resp.status, undefined);
    return resp.blob();
  },
};

/**
 * Unwrap an enveloped reply.
 *
 * Most of `/api/user/*` and `/api/auth/*` answer `{data, status}`; the game routes and
 * the catalogs answer bare. Which is which is a property of the endpoint, so the call
 * site says it rather than this module guessing from the shape.
 */
export async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = await promise;
  return (asEnvelope(body)?.data ?? null) as T;
}

// ---- proactive refresh ----

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload)) as { exp?: unknown };
    // The server is required to send `exp` as seconds-since-epoch, a number. A string
    // would disable this path silently, which is why the server has a test for it.
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function rescheduleRefresh(account: Account | null): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!account?.accessToken || !account.refreshToken) return;

  const expMs = decodeExpMs(account.accessToken);
  if (expMs === null) return;

  const delay = expMs - Date.now() - REFRESH_LEAD_TIME_MS;
  // Already inside the lead time: leave it to the reactive 401 path rather than firing
  // a refresh during render.
  if (delay <= 0) return;

  refreshTimer = setTimeout(() => {
    const current = getAccount();
    if (current) void refreshAccessToken(current).catch(() => undefined);
  }, delay);
}

accountStore.subscribe(() => rescheduleRefresh(getAccount()));
rescheduleRefresh(getAccount());
